/**
 * Shim for @vertex-protocol/utils
 * Re-implements the minimal utilities used by the Vertex frontend,
 * adapted to use BigNumber from bignumber.js (as @nadohq/client does).
 */
const BigNumber = require('bignumber.js').default || require('bignumber.js');

// ---- BigDecimal helpers ----

function toBigDecimal(val) {
  if (val === undefined || val === null || val === '') return new BigNumber(0);
  return new BigNumber(val);
}

function toFixed(val, decimals) {
  return toBigDecimal(val).toFixed(decimals);
}

function addDecimals(val, decimals) {
  if (val === undefined || val === null) return new BigNumber(0);
  return new BigNumber(val).multipliedBy(new BigNumber(10).pow(decimals ?? 18));
}

function removeDecimals(val, decimals) {
  if (val === undefined || val === null) return new BigNumber(0);
  return new BigNumber(val).dividedBy(new BigNumber(10).pow(decimals ?? 18));
}

function fromX18(val) {
  if (val === undefined || val === null) return new BigNumber(0);
  return new BigNumber(val).dividedBy(new BigNumber(10).pow(18));
}

// BigDecimals namespace — constants and helper functions on BigDecimal
const BigDecimals = {
  ZERO: new BigNumber(0),
  ONE: new BigNumber(1),
  TWO: new BigNumber(2),
  NEGATIVE_ONE: new BigNumber(-1),
  // Maximum value of a signed 128-bit integer, used as a sentinel for "empty ask" in orderbook
  MAX_I128: new BigNumber('170141183460469231731687303715884105727'),
  fromPercent(pct) {
    return new BigNumber(pct).dividedBy(100);
  },
  toPercent(val) {
    return new BigNumber(val).multipliedBy(100);
  },
  abs(val) {
    return new BigNumber(val).abs();
  },
  isMathematicalInteger(val) {
    return new BigNumber(val).isInteger();
  },
};

function sumBigDecimalBy(arr, fn) {
  if (!arr) return new BigNumber(0);
  return arr.reduce((acc, item) => {
    const val = fn(item);
    return acc.plus(new BigNumber(val === undefined || val === null ? 0 : val));
  }, new BigNumber(0));
}

// ---- Async helpers ----

async function asyncResult(promise) {
  try {
    const data = await promise;
    return [data, undefined];
  } catch (error) {
    return [undefined, error];
  }
}

// ---- Time utilities ----

const TimeInSeconds = {
  MINUTE: 60,
  HOUR: 3600,
  DAY: 86400,
  YEAR: 31536000,
};

function nowInSeconds() {
  return Math.floor(Date.now() / 1000);
}

// ---- Validation helpers ----

// Use the real implementation from @nadohq/shared — it throws on invalid addresses,
// matching the original @vertex-protocol/utils behavior.
const { getValidatedAddress } = require('@nadohq/shared');

function getValidatedHex(hex) {
  return hex;
}

// ---- Object helpers ----

function toPrintableObject(obj) {
  if (obj === null || obj === undefined) return obj;
  if (BigNumber.isBigNumber(obj)) return obj.toFixed();
  if (typeof obj === 'bigint') return obj.toString();
  if (Array.isArray(obj)) return obj.map(toPrintableObject);
  if (typeof obj === 'object') {
    const result = {};
    for (const [k, v] of Object.entries(obj)) {
      result[k] = toPrintableObject(v);
    }
    return result;
  }
  return obj;
}

module.exports = {
  toBigDecimal,
  toFixed,
  addDecimals,
  removeDecimals,
  fromX18,
  BigDecimal: BigNumber,
  BigDecimals,
  sumBigDecimalBy,
  asyncResult,
  TimeInSeconds,
  nowInSeconds,
  getValidatedAddress,
  getValidatedHex,
  toPrintableObject,
  VERTEX_PRODUCT_DECIMALS: 18,
};
